import { formatPriceWithCurrency } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getCardDate } from '~/utils/card-utils/card-utils';
import { CardPill } from '~/views/stream/components/stream-card/components/card-pill/CardPill';
import { CardType } from '~/views/stream/components/stream-card/StreamCard';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import styles from '../../OverviewEventFeed.module.scss';
import { formatScheduledDate, parseEarningsData } from './utils';

export interface EarningsRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
}

export const EarningsRow = ({ event, isLast }: EarningsRowProps) => {
  const { t } = useTranslation(['whatsNew', 'earnings', 'calendar']);
  const { openDrawer } = useEarningsInsightDrawer();

  const isPreview = event.metadata.earnings_type === 'preview';
  const cardType = isPreview
    ? CardType.EarningsPreview
    : CardType.EarningsRecap;
  const earningsId = event.metadata.earnings_id ?? undefined;

  const { data: earningsData } = useGetEarningById(earningsId);
  const { epsValue, epsDelta, currency, reportingDate, isBeat } =
    parseEarningsData(isPreview, earningsData);

  const handleClick = () => {
    if (earningsId) {
      openDrawer({
        entityTag: event.entity,
        earningsId,
        subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    }
  };

  return (
    <div
      className={clsx(styles.feedItemWrapper, {
        [styles.last]: isLast,
      })}
    >
      <div className={styles.feedItem} onClick={handleClick}>
        <div className={styles.feedItemHeader}>
          <CardPill type={cardType} />
        </div>
        <p className={styles.insightTitle}>{event.title}</p>
        <div className={styles.statsRow}>
          <div className={styles.statsContainer}>
            {epsValue !== undefined && (
              <div className={styles.earningsLabelWrapper}>
                <span className={styles.statLabel}>
                  {isPreview
                    ? t('earnings:earningsInsight.drawer.epsExpectations')
                    : t('earnings:earningsInsight.drawer.epsActual')}
                </span>
                <span className={styles.priceValue}>
                  {formatPriceWithCurrency(epsValue, currency)}
                </span>
              </div>
            )}
            {isPreview && reportingDate && (
              <>
                <div className={styles.verticalDivider} />
                <div className={styles.earningsLabelWrapper}>
                  <span className={styles.scheduledLabel}>
                    {t('calendar:scheduledFor')}
                  </span>
                  <span className={styles.scheduledDate}>
                    {formatScheduledDate(new Date(reportingDate))}
                  </span>
                </div>
              </>
            )}
            {!isPreview && epsDelta !== undefined && epsValue !== undefined && (
              <>
                <div className={styles.verticalDivider} />
                {isBeat ? (
                  <div className={styles.earningsLabelWrapper}>
                    <span className={styles.bullishValue}>{epsDelta}%</span>
                    <span className={styles.bullishText}>
                      {t('earnings:earningsInsight.drawer.beat')}
                    </span>
                  </div>
                ) : (
                  <div className={styles.earningsLabelWrapper}>
                    <span className={styles.bearishValue}>
                      {Math.abs(epsDelta)}%
                    </span>
                    <span className={styles.bearishText}>
                      {t('earnings:earningsInsight.drawer.miss')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <span className={styles.timeAgo}>
            {getCardDate(new Date(event.created_at))}
          </span>
        </div>
      </div>
    </div>
  );
};
